import { reportError } from 'src/easyclaim.app.libs/reportError';
import numbro from 'numbro';
import { transparentize } from 'polished';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { AppState } from 'src/AppState';
import styled from 'styled-components';

import { IconSearch } from '../easyclaim.app.assets/index';
import {
	CalculationDetailsModal,
	DetailContainer,
	Link,
	LinkContainer,
	StyledCalculationIcon,
} from '../easyclaim.app.components/index';

import { BankAccountDetails } from './BankAccountDetails';
import { ScreenComponent } from './ScreenComponent';

export interface ApprovalState {
	isCalculationDetailsModalOpen: boolean;
}

export class Approval extends ScreenComponent<AppState, ApprovalState> {
	constructor(props: AppState) {
		super(props);

		this.state = {
			isCalculationDetailsModalOpen: false,
		};

		this.onApprove = this.onApprove.bind(this);
		this.onDisapprove = this.onDisapprove.bind(this);
	}

	async onApprove() {
		await this.props.appService.onApproveAudit();
		await this.props.onNext();
	}

	async onDisapprove() {
		await this.props.appService.onDisapproveAudit();
		await this.props.onNext();
	}

	public render() {
		const calculation = this.props.easyClaimCalculationResult;

		const canShowCalculationDetail =
			calculation &&
			(calculation.sparePartCosts ||
				calculation.labourCosts ||
				calculation.lacquerCosts ||
				calculation.additionalCosts)
				? true
				: false;

		const allowDownloadCalculationFile = this.props.appService.isCalculationFileDownloadingAllowed(
			this.props
		);

		return (
			<div className="main-container">
				<h2 className="approval-title">
					<FormattedMessage id="approval.headline" />
				</h2>
				<DetailContainer>
					<RepairCostsContainer>
						<CostsPositionsContainer>
							{calculation && !!calculation.sparePartCosts && (
								<CostPosition>
									<Name>
										<FormattedMessage id="approval.sparePartCosts" />
									</Name>
									<Amount>
										{numbro(calculation.sparePartCosts).formatCurrency({ mantissa: 2 })}
									</Amount>
								</CostPosition>
							)}
							{calculation && !!calculation.lacquerCosts && (
								<CostPosition>
									<Name>
										<FormattedMessage id="approval.lacquerCosts" />
									</Name>
									<Amount>
										{numbro(calculation.lacquerCosts).formatCurrency({ mantissa: 2 })}
									</Amount>
								</CostPosition>
							)}
							{calculation && !!calculation.lacquerMatCosts && (
								<CostPosition>
									<Name>
										<FormattedMessage id="approval.lacquerMatCosts" />
									</Name>
									<Amount>
										{numbro(calculation.lacquerMatCosts).formatCurrency({ mantissa: 2 })}
									</Amount>
								</CostPosition>
							)}
							{calculation && !!calculation.labourCosts && (
								<CostPosition>
									<Name>
										<FormattedMessage id="approval.labourCosts" />
									</Name>
									<Amount>{numbro(calculation.labourCosts).formatCurrency({ mantissa: 2 })}</Amount>
								</CostPosition>
							)}
							{calculation && !!calculation.smallCosts && (
								<CostPosition>
									<Name>
										<FormattedMessage id="approval.smallCosts" />
									</Name>
									<Amount>{numbro(calculation.smallCosts).formatCurrency({ mantissa: 2 })}</Amount>
								</CostPosition>
							)}
							{calculation && !!calculation.additionalCosts && (
								<CostPosition>
									<Name>
										<FormattedMessage id="approval.additionalCosts" />
									</Name>
									<Amount>
										{numbro(calculation.additionalCosts).formatCurrency({ mantissa: 2 })}
									</Amount>
								</CostPosition>
							)}
						</CostsPositionsContainer>
						<CostsPositionsContainer>
							<CostPosition>
								<Name>
									<FormattedMessage id="approval.repairAmount" />
								</Name>
								<Amount>
									<strong>
										{calculation &&
											calculation.repairAmount !== null &&
											calculation.repairAmount !== undefined &&
											numbro(calculation.repairAmount).formatCurrency({ mantissa: 2 })}
									</strong>
								</Amount>
							</CostPosition>
							<CostPosition>
								<Name>
									<FormattedMessage id="approval.tax" />
								</Name>
								<Amount>
									{calculation &&
										calculation.repairAmount !== null &&
										calculation.repairAmount !== undefined &&
										calculation.repairAmountGross !== null &&
										calculation.repairAmountGross !== undefined &&
										numbro(calculation.repairAmountGross - calculation.repairAmount).formatCurrency(
											{ mantissa: 2 }
										)}
								</Amount>
							</CostPosition>
							<CostPosition>
								<Name>
									<FormattedMessage id="approval.repairAmountGross" />
								</Name>
								<Amount>
									<strong>
										{calculation &&
											calculation.repairAmountGross !== null &&
											calculation.repairAmountGross !== undefined &&
											numbro(calculation.repairAmountGross).formatCurrency({ mantissa: 2 })}
									</strong>
								</Amount>
							</CostPosition>
							{calculation && !!calculation.retention && (
								<CostPosition>
									<Name>
										<FormattedMessage id="approval.retention" />
									</Name>
									<Amount>{numbro(calculation.retention).formatCurrency({ mantissa: 2 })}</Amount>
								</CostPosition>
							)}
						</CostsPositionsContainer>
					</RepairCostsContainer>
					{(canShowCalculationDetail || allowDownloadCalculationFile) && (
						<LinkContainer>
							{canShowCalculationDetail && (
								<Link onClick={() => this.setState({ isCalculationDetailsModalOpen: true })}>
									<StyledSearchIcon />
									<Label>
										<FormattedMessage id="label.details" />
									</Label>
								</Link>
							)}
							{/* TODOKG: Check if required
                            {(allowDownloadCalculationFile && <Link
                                onClick={() =>
                                    calculation &&
                                    calculation.reportUrl &&
                                    window.open(calculation.reportUrl, '_blank')
                                }
                            >
                                <StyledCalculationIcon />
                                <Label>
                                    <FormattedMessage id="approval.calculationPDF" />
                                </Label>
                            </Link>)
                            } */}
						</LinkContainer>
					)}
				</DetailContainer>
				<BankAccountDetails
					bankAccountNumber={this.props.easyClaimCase?.bankAccountNumber}
					bankAccountOwnerName={this.props.easyClaimCase?.bankAccountOwnerName}
				/>
				<footer>
					<ButtonContainer>
						<button
							onClick={() => {
								void this.onApprove().catch((error: unknown) => {
									reportError(error, { context: 'Approval.approvalAction' });
								});
							}}
						>
							<FormattedMessage id="approval.button.approve" />
						</button>
						<button
							className="button-secondary"
							onClick={() => {
								void this.onDisapprove().catch((error: unknown) => {
									reportError(error, { context: 'Approval.approvalAction' });
								});
							}}
						>
							<FormattedMessage id="approval.button.disapprove" />
						</button>
					</ButtonContainer>
				</footer>
				{canShowCalculationDetail && (
					<CalculationDetailsModal
						isOpen={this.state.isCalculationDetailsModalOpen}
						onClose={() => this.setState({ isCalculationDetailsModalOpen: false })}
						easyClaimCalculationResult={calculation}
					/>
				)}
			</div>
		);
	}
}

const ButtonContainer = styled.div`
	display: grid;
	grid-row-gap: 1rem;
	margin-top: 1rem;
`;

const StyledSearchIcon = styled(IconSearch)`
	height: 2rem;
	width: 2rem;
	flex-grow: 0;
	flex-shrink: 0;
	margin-right: 0.35rem;
`;

const Label = styled.div`
	flex-grow: 1;
	flex-shrink: 1;
	text-transform: uppercase;
`;

const Amount = styled.div<{ fontSize?: string }>`
	font-size: ${(p) => p.fontSize || '1em'};
	flex-grow: 0;
	flex-shrink: 0;
`;

const Name = styled.div`
	padding-right: 1rem;
	hyphens: auto;
`;

const RepairCostsContainer = styled.div`
	margin-bottom: 2rem;
`;

const CostPosition = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin: 0.35rem 0;
`;

const CostsPositionsContainer = styled.div`
	display: flex;
	flex-direction: column;
	&:not(:last-child) {
		border-bottom: 1px solid ${(p) => transparentize(0.8, p.theme.colors.secondary)};
		padding-bottom: 0.5rem;
	}
	&:not(:first-child) {
		padding-top: 0.5rem;
	}
`;
